import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { billing } from '../lib/api';
import { funnelEvent, currentFunnel } from '../lib/funnel';
import { Logo } from '../components/Logo';
import { VerifyEmailNotice } from '../components/VerifyEmailNotice';
import type { ApiError } from '../lib/api';

const KNOWN = ['monthly', 'annual', 'lifetime', 'onetime'] as const;
type Plan = (typeof KNOWN)[number];

/**
 * Deep-link checkout entry for the WEB funnel (homepage "Get Riffloop Pro" →
 * studio.riffloop.app/checkout?plan=onetime). Auth-gated because a purchase must
 * attach to an account — attribution is by billing email, which the backend fills
 * from the signed-in session. Flow:
 *   - already Pro   → /dashboard (nothing to buy)
 *   - anonymous     → /login, PRESERVING the checkout intent through the OAuth
 *                     round-trip (router state alone doesn't survive it), then
 *                     the backend redirects back here and we resume.
 *   - signed-in free → POST /api/billing/checkout-session → redirect to PayPro.
 * The extension does NOT use this — it checks out PayPro directly (it already has
 * the user's email via chrome.identity). This route is the web equivalent.
 */
export function Checkout() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [err, setErr] = useState(false);
  // Checkout refused because the address hasn't been confirmed yet. Its own
  // state, not the generic error: there's a specific thing they can do about it.
  const [needsVerify, setNeedsVerify] = useState<{ detail?: string } | null>(null);
  const started = useRef(false);

  const raw = params.get('plan') || 'onetime';
  const plan: Plan = (KNOWN as readonly string[]).includes(raw) ? (raw as Plan) : 'onetime';

  useEffect(() => {
    if (loading || started.current) return;
    started.current = true;

    // Already Pro — nothing to purchase.
    if (user && user.plan === 'pro') {
      navigate('/dashboard', { replace: true });
      return;
    }

    // Anonymous — sign in first, carrying the intent so we land back here after
    // OAuth and resume. `next` stays a same-origin path (guarded again in Login).
    if (!user) {
      // Which funnel sent them (the splitter's plan modal marks itself) — so
      // /admin can follow an anonymous preview all the way to a payment.
      funnelEvent('rl_checkout_login', { plan, funnel: currentFunnel() }, 'checkout');
      const next = '/checkout?plan=' + encodeURIComponent(plan);
      navigate('/login?next=' + encodeURIComponent(next), { replace: true });
      return;
    }

    // Signed-in free user — mint the PayPro session (backend prefills
    // billing-email + x-uid from the account) and hand off.
    (async () => {
      funnelEvent('rl_checkout_start', { plan, funnel: currentFunnel() }, 'checkout');
      try {
        const { url } = await billing.checkoutSession(plan);
        window.location.href = url;
      } catch (e) {
        const api = e as ApiError;
        funnelEvent('rl_checkout_error', { plan, funnel: currentFunnel(), code: api?.code || 'unknown' }, 'checkout');
        if (api?.code === 'email_unverified') {
          setNeedsVerify({ detail: api.detail });
          return;
        }
        setErr(true);
      }
    })();
  }, [loading, user, plan, navigate]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 20,
        padding: 24,
        textAlign: 'center',
      }}
    >
      <Logo size="lg" variant="horizontal" />
      {needsVerify ? (
        <VerifyEmailNotice detail={needsVerify.detail} />
      ) : (
        <>
          {!err && <div className="rl-spinner" />}
          <p style={{ color: 'var(--text-dim)', fontSize: 14, maxWidth: 340, lineHeight: 1.5 }}>
            {err
              ? 'Could not start checkout. Please refresh and try again.'
              : 'Starting your checkout…'}
          </p>
        </>
      )}
    </div>
  );
}
